/**
 * The Rich Text Editor (piece 5). Built once here, in the earliest phase that
 * needs it, and shared by three consumers: the Email Template composer in this
 * piece, the Knowledge Base Article Editor, and the mail Compose screen.
 * Building the editor here removes a backward dependency; the article editor
 * adds a style menu, a table of contents and the rest of a word processor's
 * set to the core (decisions 0750, 0800).
 */

export const CORE_TOOLS = [
  'heading',
  'bold',
  'italic',
  'underline',
  'strikethrough',
  'bulletList',
  'numberList',
  'image',
  'alignLeft',
  'alignCenter',
  'alignRight',
  'alignJustify',
] as const;

/**
 * 🔒 The text set (decision 0704): the core less alignment and pictures, for a
 * consumer whose stored allowlist keeps neither — the terms. A tool offered
 * there would format something the save then silently drops.
 */
export const TEXT_TOOLS = ['heading', 'bold', 'italic', 'underline', 'strikethrough', 'bulletList', 'numberList'] as const;

/**
 * Email-only additions, because an email template is composed for appearance
 * as much as structure where a Knowledge Base article is composed primarily
 * for structure.
 */
export const EMAIL_TOOLS = ['fontFamily', 'fontSize', 'textColor', 'highlightColor'] as const;

export type CoreTool = (typeof CORE_TOOLS)[number];
export type EmailTool = (typeof EMAIL_TOOLS)[number];
export type RichTextTool = CoreTool | EmailTool;

/**
 * 🔒 The email consumers' toolbar, the full set a mail program draws, in its
 * order and its groups (decision 1537): undo and redo, the font and its size,
 * the four marks, the two colours, the four alignments, the two lists, outdent
 * and indent, line spacing, a link, a picture and a table, a quotation, and
 * clear formatting. 🔨 The heading toggle is not among them; a mail program's
 * toolbar draws a size, never a heading. Insert signature and Insert
 * Placeholder follow where the consumer hands a signature or placeholders in.
 */
export const EMAIL_TOOLBAR_GROUPS = [
  ['undo', 'redo'],
  ['fontFamily', 'fontSize'],
  ['bold', 'italic', 'underline', 'strikethrough'],
  ['textColor', 'highlightColor'],
  ['alignLeft', 'alignCenter', 'alignRight', 'alignJustify'],
  ['bulletList', 'numberList'],
  ['outdent', 'indent'],
  ['lineSpacing'],
  ['link', 'image', 'table'],
  ['quote'],
  ['clearFormatting'],
] as const;
export type EmailToolbarTool = (typeof EMAIL_TOOLBAR_GROUPS)[number][number];
export const EMAIL_TOOLBAR_TOOLS: readonly EmailToolbarTool[] = EMAIL_TOOLBAR_GROUPS.flat();

/**
 * 🔒 The four line spacings the email toolbar offers (decision 1537), written on
 * a block as its `line-height`. 🔨 Each is written as the number itself and never
 * as a design token: a sent message is drawn by another program, which knows no
 * token of ours.
 */
export const LINE_SPACINGS = ['1', '1.15', '1.5', '2'] as const;
export type LineSpacing = (typeof LINE_SPACINGS)[number];

export type RichTextConsumer = 'emailTemplate' | 'knowledgeBase' | 'mailCompose';

/** Block alignment is core, not an email-only addition: a template's logo line is commonly centered, and so is an article's. */
export const ALIGNMENTS = ['alignLeft', 'alignCenter', 'alignRight', 'alignJustify'] as const;

/**
 * 🔒 The article's additions (decisions 0750, 0800): the style menu in place of
 * the heading toggle, Insert Table of Contents, the size menu, text colour and
 * highlight, a link, Insert a table and a horizontal rule. Its picture is the
 * core's image tool, uploading a File rather than inlining the bytes. Indent
 * and outdent left the toolbar for the list bar (decision 1270).
 */
export const ARTICLE_TOOLS = [
  'headingStyle', 'toc', 'fontSize', 'textColor', 'highlightColor', 'link', 'table', 'horizontalRule',
] as const;
export type ArticleTool = (typeof ARTICLE_TOOLS)[number];

/**
 * 🔒 The table bar's controls (T397; decision 1240): they live in the bar that
 * opens under the toolbar while the caret stands in a table, and nowhere on the
 * toolbar itself.
 */
export const ARTICLE_TABLE_BAR_TOOLS = [
  'tableRowAbove', 'tableRowBelow', 'tableRowRemove', 'tableColumnLeft', 'tableColumnRight', 'tableColumnRemove', 'tableHeader', 'tableDelete',
] as const;

/**
 * 🔒 The picture bar's controls (T372; decision 1243): the four named sizes and
 * the typed percent, the desired minimum in pixels (decision 1942), the three
 * alignments of the picture's own line, its alt text, its caption (decision
 * 1476), and Remove Picture.
 */
export const ARTICLE_PICTURE_BAR_TOOLS = [
  'pictureSmall', 'pictureMedium', 'pictureLarge', 'pictureFull', 'picturePercent', 'pictureMinWidth', 'pictureLeft', 'pictureCentre', 'pictureRight', 'pictureAlt', 'pictureCaption', 'pictureRemove',
] as const;

/**
 * 🔒 The list bar's controls (T397; decisions 1270, 1271): the list's kind,
 * its start number, indent and outdent, and Remove List. The two list buttons
 * stay on the toolbar, which is where a list is started.
 */
export const ARTICLE_LIST_BAR_TOOLS = ['listBulleted', 'listNumbered', 'listStart', 'listIndent', 'listOutdent', 'listRemove'] as const;
export type ArticleBarTool = (typeof ARTICLE_TABLE_BAR_TOOLS)[number] | (typeof ARTICLE_PICTURE_BAR_TOOLS)[number] | (typeof ARTICLE_LIST_BAR_TOOLS)[number];

/**
 * 🔒 The toolbar a consumer draws, which is exactly what its stored allowlist
 * keeps (decision 0800): the article's is the core less the heading toggle,
 * plus its additions; the email consumers' is the full set of decision 1537.
 */
export function toolsFor(consumer: RichTextConsumer): readonly (RichTextTool | ArticleTool | EmailToolbarTool)[] {
  if (consumer === 'knowledgeBase') return [...CORE_TOOLS.filter((tool) => tool !== 'heading'), ...ARTICLE_TOOLS];
  // 🔒 The email consumers draw the full set of decision 1537, which carries every email addition and the core less the heading toggle.
  return EMAIL_TOOLBAR_TOOLS;
}

/** 🔒 The controls a consumer's object bars draw (decision 1240): the article's alone, and none for the email consumers. */
export function barToolsFor(consumer: RichTextConsumer): readonly ArticleBarTool[] {
  return consumer === 'knowledgeBase' ? [...ARTICLE_TABLE_BAR_TOOLS, ...ARTICLE_PICTURE_BAR_TOOLS, ...ARTICLE_LIST_BAR_TOOLS] : [];
}

/**
 * What each tool does to the selection. A toolbar button that changes nothing
 * is the defect this table exists to prevent: the first build of the editor
 * rendered all twelve core tools and none of them applied anything, and a
 * suite that asked only whether the buttons were there passed over it (found
 * while building 2026-09-08).
 */
export const TOOL_COMMANDS: Readonly<Record<RichTextTool, string>> = {
  heading: 'formatBlock',
  bold: 'bold',
  italic: 'italic',
  underline: 'underline',
  strikethrough: 'strikeThrough',
  bulletList: 'insertUnorderedList',
  numberList: 'insertOrderedList',
  image: 'insertImage',
  alignLeft: 'justifyLeft',
  alignCenter: 'justifyCenter',
  alignRight: 'justifyRight',
  alignJustify: 'justifyFull',
  fontFamily: 'fontName',
  fontSize: 'fontSize',
  textColor: 'foreColor',
  highlightColor: 'hiliteColor',
};

/** A tool the person answers with a value, rendered as a picker rather than a toggle. */
export const VALUE_TOOLS: readonly RichTextTool[] = ['fontFamily', 'fontSize', 'textColor', 'highlightColor'];

export function takesValue(tool: RichTextTool): boolean {
  return VALUE_TOOLS.includes(tool);
}

/** A tool whose pressed state the editing surface can be asked about. */
export const TOGGLE_TOOLS: readonly RichTextTool[] = [
  'bold', 'italic', 'underline', 'strikethrough', 'bulletList', 'numberList', 'alignLeft', 'alignCenter', 'alignRight', 'alignJustify',
];

export function isToggle(tool: RichTextTool): boolean {
  return TOGGLE_TOOLS.includes(tool);
}

/** Heading toggles the block between a heading and a paragraph. */
export const HEADING_BLOCK = 'h2';
export const PARAGRAPH_BLOCK = 'p';

export function headingBlock(isHeading: boolean): string {
  return isHeading ? PARAGRAPH_BLOCK : HEADING_BLOCK;
}

/** The families the email consumers offer. */
export const FONT_FAMILIES = ['Arial', 'Georgia', 'Helvetica', 'Times New Roman', 'Verdana'] as const;

/**
 * 🔒 The one ladder of point sizes every Size menu offers (T863; decision 1615):
 * the email editor's and the article editor's alike, the word processor's
 * default ladder. A size is written as a span's `font-size` in points, which
 * every mail program and the PDF read as the same size.
 */
export const FONT_POINT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 36] as const;
export type FontPointSize = (typeof FONT_POINT_SIZES)[number];

/** 🔒 The size the menu draws selected where a run names none; choosing it writes no size at all (decision 1615). */
export const DEFAULT_FONT_POINT_SIZE: FontPointSize = 12;

/** A size as the one declaration value it is written as, `12pt`. */
export function fontPointValue(size: FontPointSize): string {
  return `${size}pt`;
}

/** A `font-size` value read back as a size of the ladder, or null for anything else: pixels, ems, a size off the ladder. */
export function fontPointSizeOf(raw: string): FontPointSize | null {
  const match = /^\s*(\d{1,2})pt\s*$/i.exec(raw);
  if (!match) return null;
  const points = Number(match[1]);
  return (FONT_POINT_SIZES as readonly number[]).includes(points) ? (points as FontPointSize) : null;
}

/**
 * 🔒 The seven sizes of the browser's `<font size>`, which the email editor wrote
 * before decision 1615. A stored draft or a received message still carries
 * them, so the sanitizer keeps them; nothing writes one any more.
 */
export const FONT_SIZES = ['1', '2', '3', '4', '5', '6', '7'] as const;
export type LegacyFontSize = (typeof FONT_SIZES)[number];

/**
 * 🔒 The point size each legacy step is drawn as (decision 1615): the browser's
 * own pixel size for the step at three quarters of a point a pixel, to the
 * nearest size on the ladder. The menu reads a legacy run by it, and the editor
 * rewrites a legacy run as its point span the first time a size is chosen.
 */
export const LEGACY_FONT_SIZE_POINTS: Readonly<Record<LegacyFontSize, FontPointSize>> = {
  '1': 8, '2': 10, '3': 12, '4': 14, '5': 18, '6': 24, '7': 36,
};

/**
 * The editing surface keeps the browser's spell check and never swallows its
 * correction menu. Nothing is bundled: the check is that nothing is switched
 * off, which is why this is a constant a test can read rather than a feature.
 */
export const SPELLCHECK_ON = true;
export const OVERRIDES_CONTEXT_MENU = false;

/**
 * One editing surface and no separate authoring format. Formatting, images and
 * placeholders carry through to the sent message unchanged; nothing is
 * converted with loss at send time, which is what stops a message that looked
 * right in the editor arriving as plain text.
 */
export const CONVERTS_AT_SEND = false;

export function carriesThroughUnchanged(composed: string): string {
  return composed;
}
